import { yupResolver } from "@hookform/resolvers/yup";
import * as Device from "expo-device";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Platform } from "react-native";
import * as yup from "yup";

import NotesFormView from "./NotesFormView";
import { IForm } from "@/features/notes/type";
import useAutoShareWithManagers from "@/features/notes/hooks/useAutoShareWithManagers";
import { handleCreateOrDeleteNote } from "@/features/notes/utils/handleDataNote";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import { useNotesCreateNoteMutation } from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useToastNotification } from "@/hooks/useToastNotification";
import {
  NoteActionsheetState,
  noteActionsheetStateVar,
} from "@/stores/noteActionsheetState";
import { ifWeb } from "@/utils/platform";

/** Maximum length of a note body (SE-5208: raised from 300 to align with backend) */
export const NOTE_BODY_MAX_LENGTH = 5000;

export const notesFormSchema = yup.object().shape({
  title: yup.string(),
  body: yup
    .string()
    .max(
      NOTE_BODY_MAX_LENGTH,
      `Note must be ${NOTE_BODY_MAX_LENGTH} characters or less`
    )
    .required("Can't be blank"),
  playerId: yup.string().required("Can't be blank"),
});

/**
 *
 */
interface Props {
  playerId: string;
}

/**
 * Container component for note creation form with validation and submission.
 * Auto-shares newly created notes with organization managers and admins
 * so they receive notifications and appear in the shared recipients list.
 * Guards the success flow (toast, analytics, form reset) behind a noteId
 * check so partial server responses don't trigger side effects.
 * @param root0 - Component props
 * @param root0.playerId - ID of the player for which note is being created
 */
const NotesFormContainer = ({ playerId }: Props) => {
  const toast = useToastNotification();
  const currentUser = useCurrentUser();
  const recordUserGoal = useRecordUserGoal({
    goalType: GoalType.CreateNote,
    value: "",
  });
  const [create] = useNotesCreateNoteMutation();
  const { shareNoteWithManagers } = useAutoShareWithManagers();
  const isSmallScreen = useMobileScreen();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IForm>({
    mode: "onChange",
    resolver: yupResolver(notesFormSchema),
    defaultValues: {
      body: "",
      title: "",
      playerId,
    },
  });

  const submit = useCallback(
    async ({ body, playerId }: IForm) => {
      const result = await create({
        variables: {
          input: { body, title: "", playerId },
        },
        update: (cache, { data, errors }) =>
          handleCreateOrDeleteNote("create", cache, data, errors, playerId),
        optimisticResponse: {
          __typename: "Mutation",
          createNote: {
            __typename: "Note",
            body,
            createdAt: new Date().toISOString(),
            id: `temp-id-${new Date().toISOString()}`,
            isPublic: true,
            playerId,
            title: "",
            updatedAt: new Date().toISOString(),
            userId: currentUser?.id ?? "",
            user: {
              __typename: "User",
              id: currentUser?.id ?? "",
              cognitoUser: null,
            },
          },
        },
      });

      const noteId = result.data?.createNote?.id;
      if (!noteId) return;

      await shareNoteWithManagers(noteId, "NotesForm");

      recordUserGoal({
        value: result.data?.createNote?.playerId ?? "",
        goalType: GoalType.CreateNote,
      });
      reset({
        body: "",
        title: "",
        playerId,
      });
      noteActionsheetStateVar(NoteActionsheetState.List);
      toast.show({
        action: "success",
        description: "Note added to list",
      });
      AnalyticsTracker.note.logNoteCreated({
        user_id: currentUser?.id || "",
        organization_id: currentUser?.organizationId || "",
        note_type: "text",
        content_length: body.length,
        phone_number: currentUser?.phoneNumber || "",
        note_id: noteId,
        player_id: playerId,
        title: "",
        body,
        is_shared: true,
        device_name: Device.deviceName || "Unknown Device",
        device_os: Device.osName || "Unknown OS",
        creation_source: ifWeb("web", "mobile"),
        platform: ifWeb("web", Platform.OS),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    },
    [create, recordUserGoal, toast, currentUser, reset, shareNoteWithManagers]
  );

  const doSubmit = handleSubmit(submit);

  return (
    <NotesFormView
      onSubmit={doSubmit}
      isValid={isValid}
      control={control}
      errors={errors}
      loading={isSubmitting}
      isSmallScreen={isSmallScreen}
    />
  );
};

export default NotesFormContainer;
